import { WorkspaceType } from '@flowx/designer/modules/workspaces/shared/commons.types'

import { ExtendedNotification } from '../notifications.model'

export enum NotificationPeriod {
  TODAY = 'TODAY',
  THIS_WEEK = 'THIS WEEK',
  OLDER = 'OLDER',
}

export interface NotificationGroup {
  period: NotificationPeriod
  notifications: ExtendedNotification[]
}

const PERIOD_ORDER = [
  NotificationPeriod.TODAY,
  NotificationPeriod.THIS_WEEK,
  NotificationPeriod.OLDER,
] as const

export const NOTIFICATION_RETENTION_DAYS = 30

const MINUTE = 60 * 1000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR
const WEEK = 7 * DAY

export const MIN_SEARCH_LENGTH = 2

/** The filters that are not workspace-scoped; the rest of the union is `WorkspaceType` itself. */
export enum NotificationFilterMode {
  ALL = 'ALL',
  UNREAD = 'UNREAD',
}

export type NotificationFilter = NotificationFilterMode | WorkspaceType

export const DEFAULT_NOTIFICATION_FILTER: NotificationFilter = NotificationFilterMode.ALL

export const searchAndFilterNotifications = (
  notifications: ExtendedNotification[],
  query: string,
  filter: NotificationFilter
): ExtendedNotification[] => {
  const filtered =
    filter === NotificationFilterMode.ALL
      ? notifications
      : notifications.filter(({ markedRead, workspaceType }) =>
          filter === NotificationFilterMode.UNREAD ? !markedRead : workspaceType === filter
        )

  const term = query.trim().toLowerCase()

  return term.length < MIN_SEARCH_LENGTH
    ? filtered
    : filtered.filter(({ searchFields }) => searchFields.some((field) => field.includes(term)))
}

/**
 * Groups in fixed order, empty ones dropped, input order preserved within each — so the caller's
 * newest-first list stays newest-first inside every group.
 */
export const groupNotificationsByDate = (
  notifications: ExtendedNotification[],
  currentTime: number
): NotificationGroup[] => {
  const periods = notifications.map((notification) =>
    notificationPeriod(notification.createdTimestamp, currentTime)
  )

  return PERIOD_ORDER.map((period) => ({
    period,
    notifications: notifications.filter((_, index) => periods[index] === period),
  })).filter((group) => group.notifications.length > 0)
}

/**
 * Calendar-day based, because "today" is a calendar boundary and not an elapsed duration.
 *
 * `Math.round` rather than `Math.floor`: a daylight-saving transition makes one of those days 23 or
 * 25 hours long, so flooring the gap between two midnights can read 0.958 days as 0. A non-positive
 * gap covers a future-dated notification from a skewed clock.
 */
export const notificationPeriod = (
  createdTimestamp: number | string,
  currentTime: number
): NotificationPeriod => {
  const daysApart = Math.round(
    (startOfDay(currentTime) - startOfDay(notificationTimestampMs(createdTimestamp))) / DAY
  )

  if (daysApart <= 0) {
    return NotificationPeriod.TODAY
  }

  if (daysApart <= 7) {
    return NotificationPeriod.THIS_WEEK
  }

  return NotificationPeriod.OLDER
}

const startOfDay = (timestampMs: number): number => new Date(timestampMs).setHours(0, 0, 0, 0)

export const relativeAgeLabel = (
  createdTimestamp: number | string,
  currentTime: number
): string => {
  const elapsed = Math.max(0, currentTime - notificationTimestampMs(createdTimestamp))

  if (elapsed < MINUTE) {
    return 'now'
  }

  if (elapsed < HOUR) {
    return `${Math.floor(elapsed / MINUTE)}m`
  }

  if (elapsed < DAY) {
    return `${Math.floor(elapsed / HOUR)}h`
  }

  if (elapsed < WEEK) {
    return `${Math.floor(elapsed / DAY)}d`
  }

  return `${Math.floor(elapsed / WEEK)}w`
}

/**
 * The one place the two wire shapes of `createdTimestamp` are resolved.
 *
 * As a number it is epoch seconds with fractional nanos (`1787661650.808711248`), not milliseconds
 * — audited against the real backend payload, where dividing by 1000 landed in 1970. The other
 * serializer sends ISO-8601, which `Date.parse` already returns in milliseconds.
 */
export const notificationTimestampMs = (createdTimestamp: number | string): number =>
  typeof createdTimestamp === 'number' ? createdTimestamp * 1000 : Date.parse(createdTimestamp)
